
import { Badge, Row, Stack, Text, Time, TimeRange } from '@client/shared/design';
import { toInstant, SLOT_MINUTES } from '@shared/booking/slots.ts';
import { slotCount as rangeSlotCount, viewerDayKey } from '@client/shared/calendar/week-grid.ts';
import type { CalendarRange } from '@client/shared/hooks/use-calendar-view.ts';
import { useCalendarAxis } from '@client/shared/hooks/use-calendar-axis.ts';
import { viewerTimeZone } from '@client/shared/lib/viewer-timezone.ts';
import { cx } from '@client/shared/lib/cx.ts';
import { calendarEmptyLabel } from '@client/shared/calendar/text.ts';
import styles from './CalendarGrid.module.css';

export interface CalendarEvent {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly tone?: 'accent' | 'neutral' | 'warn';
}

export interface CalendarGridProps {
  events: readonly CalendarEvent[];
  anchor: Date;
  range?: CalendarRange;
  onSelectEvent?: (event: CalendarEvent, el: HTMLElement) => void;
  emptyLabel?: string;
  className?: string;
}

interface DayColumn {
  readonly key: string;
  readonly date: Date;
  readonly isToday: boolean;
  readonly events: readonly CalendarEvent[];
}

interface AxisHeader {
  readonly key: string;
  readonly dayStart: Date;
  readonly isToday: boolean;
}

function bucketEvents(
  events: readonly CalendarEvent[],
  headers: readonly AxisHeader[],
  zone: string,
): DayColumn[] {
  const byDay = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const key = viewerDayKey(toInstant(event.start), zone);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(event);
    else byDay.set(key, [event]);
  }

  return headers.map(({ key, dayStart, isToday }) => {
    const dayEvents = (byDay.get(key) ?? [])
      .slice()
      .sort((a, b) => toInstant(a.start).getTime() - toInstant(b.start).getTime());
    return { key, date: dayStart, isToday, events: dayEvents };
  });
}

function durationLabel(event: CalendarEvent): string {
  const blocks = Math.max(1, rangeSlotCount({ start: toInstant(event.start), end: toInstant(event.end) }));
  const minutes = blocks * SLOT_MINUTES;
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

const toneBadge = { accent: 'accent', neutral: 'neutral', warn: 'warn' } as const;

export function CalendarGrid({
  events,
  anchor,
  range = 'week',
  onSelectEvent,
  emptyLabel = calendarEmptyLabel({ scope: 'week' }),
  className,
}: CalendarGridProps) {
  const zone = viewerTimeZone();
  const { columnHeaders, zoneLabel } = useCalendarAxis({ anchor, range, zone });
  const week = bucketEvents(events, columnHeaders, zone);
  const total = week.reduce((n, col) => n + col.events.length, 0);
  const viewLabel = range === 'day' ? 'Open houses, day view' : 'Open houses, week view';

  return (
    <div className={className}>
      <div
        className={cx(styles.grid, range === 'day' && styles.gridDay)}
        role="grid"
        aria-label={zoneLabel ? `${viewLabel}, times in ${zoneLabel}` : viewLabel}
      >
        {week.map((col, i) => (
          <section
            key={col.key}
            role="gridcell"
            className={cx(styles.day, col.isToday && styles.today, i === 0 && styles.hasZone)}
            aria-current={col.isToday ? 'date' : undefined}
          >
            <header className={styles.dayHead}>
              {i === 0 && zoneLabel && (
                <span className={styles.zoneTag} aria-hidden="true">
                  {zoneLabel}
                </span>
              )}
              <span className={styles.dayName}>
                <Time at={col.date} date />
              </span>
              {col.isToday && (
                <span className={styles.todayDot} aria-label="Today">
                  Today
                </span>
              )}
            </header>

            <div className={styles.dayBody}>
              {col.events.length === 0 ? (
                <span className={styles.dayEmpty} aria-hidden="true" />
              ) : (
                col.events.map((event) => {
                  const tone = event.tone ?? 'accent';
                  const interactive = Boolean(onSelectEvent);
                  return (
                    <article
                      key={event.id}
                      className={cx(styles.event, styles[`tone-${tone}`], interactive && styles.eventClickable)}
                      {...(interactive
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            onClick: (e: React.MouseEvent<HTMLElement>) =>
                              onSelectEvent?.(event, e.currentTarget),
                            onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectEvent?.(event, e.currentTarget);
                              }
                            },
                          }
                        : {})}
                    >
                      <Row align="center" gap={2} className={styles.eventTop}>
                        <TimeRange start={event.start} end={event.end} className={styles.eventTime} />
                        <Badge tone={toneBadge[tone]}>{durationLabel(event)}</Badge>
                      </Row>
                      <Text size="sm" weight="medium" className={styles.eventTitle}>
                        {event.title}
                      </Text>
                      {event.subtitle && (
                        <Text size="xs" tone="soft" className={styles.eventSub}>
                          {event.subtitle}
                        </Text>
                      )}
                    </article>
                  );
                })
              )}
            </div>
          </section>
        ))}
      </div>

      {total === 0 && (
        <div className={styles.empty}>
          <Stack gap={1} align="center">
            <Text tone="soft" size="sm">
              {emptyLabel}
            </Text>
          </Stack>
        </div>
      )}
    </div>
  );
}
